import React from 'react';

import PersonalisationSlotHeader from '../PersonalisationSlotHeader';
import { PersonalisationCard } from '../PersonalisationCard';
import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { ComponentGroup } from '../ComponentGroups';

import styles from './personalisation-grid.module.css';

/**
 * PersonalisationComponentSlot component displays a component slot in the personalisation journey.
 *
 * @param {Object} props - Component props
 * @param {Object} props.slot - The slot object
 * @param {Object} props.componentGroups - The component groups object
 * @returns {import('react').JSX.Element | null}
 */
export default function PersonalisationComponentSlot({ slot, componentGroups }) {
  const { allowedComponentGroups = [], id: slotId } = slot;
  const { groupComponentsByProduct } = slot;
  const { variants } = usePersonalisationContext();

  if (!Array.isArray(allowedComponentGroups) || allowedComponentGroups.length === 0) return null;

  let groups = allowedComponentGroups
    // Replace the IDs with the actual items
    .map((groupId) => componentGroups.get(groupId))
    .filter((group) => group !== undefined);

  // If groupComponentsByProduct is true, render all cards in a single <ul>
  if (groupComponentsByProduct) {
    return (
      <>
        <PersonalisationSlotHeader slot={slot} />
        <fieldset>
          <ul
            className={
              styles['personalisation__selection-grid'] +
              ' ' +
              styles[`personalisation__selection-grid--${slot.columns}-column`]
            }
          >
            {groups.map((group) => {
              // Get variants for this group
              const processedGroup = group.productVariantIds
                .map((id) => variants.get(id))
                .filter((variant) => {
                  // Check if the variant does not have secondary options or if the group hero is true
                  return variant && (variant.secondaryOptions.length === 0 || variant.groupHero !== false);
                });

              // Use the first variant as the representative for the product card
              const representativeVariant = processedGroup[0];

              if (!representativeVariant) return null;

              // Create secondary options from all variants in this group
              const secondaryOptions = processedGroup.map((variant) => ({
                id: variant.id,
                title: variant.title,
                shortDescription: variant.shortDescription,
                price: variant.price,
                compareAtPrice: variant.compareAtPrice,
                image: variant.image,
                asciiSymbol: variant.asciiSymbol,
                availableForSale: variant.availableForSale
              }));

              // Checks if any of the secondary options in this group is available for sale.
              // We want to disable the parent card if none of the secondary options are available for sale.
              const isGroupAvailable = secondaryOptions.some((option) => option.availableForSale);

              // Determine if secondary options should be disabled (only one variant)
              const disableSecondaryOptions = processedGroup.length === 1;

              // Don't show the short description if the secondary options exist.
              // Secondary option cards will display the short description individually.
              const showShortDescription = disableSecondaryOptions !== false;

              return (
                <PersonalisationCard
                  key={group.id}
                  id={representativeVariant.id}
                  slotId={slotId}
                  componentGroupId={group.id}
                  imageSrc={representativeVariant.product?.featuredImage?.url}
                  imageAlt={group.uiHeading || group.internalName}
                  title={group.uiHeading || group.internalName}
                  description={showShortDescription ? representativeVariant.shortDescription?.value || null : null}
                  availableForSale={isGroupAvailable}
                  variants={group.productVariantIds}
                  price={representativeVariant.price}
                  compareAtPrice={representativeVariant.compareAtPrice}
                  secondaryOptions={secondaryOptions}
                  secondaryOptionsName={group.uiHeading || group.internalName}
                  secondaryOptionsLabel={group.defaultSelectionLabel || 'Choose option'}
                  disableSecondaryOptions={disableSecondaryOptions}
                  selectedVariantId={representativeVariant.id}
                  isSelected={false}
                  badge={group.badge}
                  helpPage={group.helpPage}
                  priceModifierType={group.priceModifierType}
                  priceModifierValue={group.priceModifierValue}
                />
              );
            })}
          </ul>
        </fieldset>
      </>
    );
  }

  // Default behavior for regular component groups (each group gets its own <ul> and separated onto a card.)
  return (
    <>
      <PersonalisationSlotHeader slot={slot} />
      <div>
        {groups.map((group) => (
          <ComponentGroup key={group.id + slotId} group={group} slot={slot} slotId={slotId} />
        ))}
      </div>
    </>
  );
}
